/**
 * Tag colours. Muted slate/steel tones rather than saturated primaries: a tag is
 * a label, not an alert, and these sit against the v4 surface without shouting.
 * Each is dark enough to stay legible as chip text on a light fill.
 */
export const defaultColors = [
  '#334155', // slate
  '#0f766e', // teal
  '#1d4ed8', // indigo
  '#6d28d9', // violet
  '#a16207', // amber
  '#b91c1c', // clay
  '#15803d', // moss
  '#0e7490', // steel
  '#9d174d', // plum
];

export const COLORS = {
  Blue: '#1769aa',
  Gray: 'gray',
  DarkGray: 'darkgray',
  Black: 'black',
  Red: 'red',
  Green: 'green',
  TorchRed: '#ff1744',
  White: 'white',
  LightGreen: '#4caf50',
  Cyan: '#4BD1C3',
  LightGrey: '#BEBEBE',
  LightOrange: '#ff9248',
  LightRed: '#DCA7B5',
  LightCyan: '#60C9E4',
  DarkCyan: '#29B6F6',
  DarkRed: '#D32F2F',
  Orange: '#ED6C02',
  Purple: '#6F19EA',
  DarkPurple: '#360977ff',
  SubtleRed: '#ffc7ce',
  SubtleGrey: '#D4D5D8',
  SubtleBlue: '#ddf7ff',
  NavyBlue: '#176878ff',
  LightNavyBlue: '#308293ff',
  // Mobile card allocation colors
  SoftRed: '#E57373', // Unallocated jobs - warm red
  SoftBlue: '#42A5F5', // Assigned to current user - blue
  MediumGrey: '#9E9E9E', // Assigned to others - gray
  // Mobile UI colors
  BackgroundGrey: '#F5F5F5', // Page backgrounds
  LightBlueBackground: '#E3F2FD', // Info/checked-in states
  LightGreenBackground: '#E8F5E9', // Success states
  LightRedBackground: '#FFEBEE', // Error/warning states
  LightYellow: '#FFF9E6', // Warning/fault highlights
  NavyBlueLight: '#4A90A4', // Lighter navy accent
  AmberOrange: '#FF9800', // Timeline part actions
  NavyBlueDark: '#0E4D5C', // Dark navy gradient endpoint (updated to match final design)
  // Priority colors (P1-P4)
  PriorityP1: '#ef4444', // Red - 2hr response
  PriorityP2: '#f59e0b', // Amber - 4hr response
  PriorityP3: '#3b82f6', // Blue - 8hr response
  PriorityP4: '#94a3b8', // Gray - 24hr response
  // Secondary button colors
  SlateLight: '#f1f5f9', // Secondary button background
  SlateMedium: '#e2e8f0', // Secondary button hover
  SlateDark: '#475569', // Secondary button text
  // SLA urgency colors (CMS1 ruleset)
  SLAUrgency6: '#7c3aed', // Purple - >48h remaining
  SLAUrgency5: '#2563eb', // Blue - 24-48h remaining
  SLAUrgency4: '#16a34a', // Green - 16-24h remaining
  SLAUrgency3: '#ca8a04', // Dark yellow - 8-16h remaining
  SLAUrgency2: '#ea580c', // Orange - 4-8h remaining
  SLAUrgency1: '#dc2626', // Red - <4h remaining
  SLAUrgency0: '#6b7280', // Dark grey - breached / stopped / met
  // SLA status colors (WCAG AA 4.5:1 contrast on white)
  SLABreached: '#dc2626', // Red - breached / <2h remaining (4.9:1)
  SLAUrgent: '#b45309', // Dark amber - 2–8h remaining (5.0:1)
  SLAComplete: '#15803d', // Dark green - met / >8h remaining (5.0:1)
  // SLAActive uses NavyBlue (#176878ff) - already defined above (5.2:1)
  SLATrackGrey: '#eef2f6', // SVG ring background track
  BrandPillBg: '#f0f4f8', // Brand pill background
  // Utility colors (borders, backgrounds, inputs)
  CardBorder: '#eeeeee', // Card/list borders
  CardBackground: '#ffffff', // Card/list backgrounds
  DisabledGrey: '#DCDCDC', // Disabled states
  LightBackground1: '#f4f4f4', // Section backgrounds (darker)
  LightBackground2: '#f7f7f7', // Section backgrounds (lighter)
  OffWhite: '#fcfdfd', // Layout backgrounds
  InputBackground: '#f0f0f0', // Input/chip backgrounds
  // V4 Status Badge colors (Slate Professional — WCAG AA)
  StatusOpenBg: '#dbeafe',
  StatusOpenText: '#1e40af',
  StatusInProgressBg: '#fef3c7',
  StatusInProgressText: '#78350f',
  StatusPendingPartsBg: '#f3e8ff',
  StatusPendingPartsText: '#6b21a8',
  StatusScheduledBg: '#eef2ff',
  StatusScheduledText: '#4338ca',
  StatusClosedBg: '#dcfce7',
  StatusClosedText: '#14532d',
  StatusCancelledBg: '#f1f5f9',
  StatusCancelledText: '#475569',
  // V4 Priority colors (Slate Professional — WCAG AA)
  PriorityCritical: '#b91c1c',
  PriorityCriticalBg: '#fef2f2',
  PriorityHigh: '#c2410c',
  PriorityHighBg: '#fff7ed',
  PriorityMedium: '#a16207',
  PriorityMediumBg: '#fffbeb',
  PriorityLow: '#047857',
  PriorityLowBg: '#ecfdf5',
  // V4 History type badge colors (Slate Professional — WCAG AA)
  HistoryNoteBg: '#dcfce7',
  HistoryNoteText: '#14532d',
  HistoryStatusBg: '#dbeafe',
  HistoryStatusText: '#1e40af',
  HistoryAssignmentBg: '#fef3c7',
  HistoryAssignmentText: '#78350f',
  HistoryPartsBg: '#f3e8ff',
  HistoryPartsText: '#6b21a8',
  HistoryAttendanceBg: '#fee2e2',
  HistoryAttendanceText: '#991b1b',
  HistorySystemBg: '#f1f5f9',
  HistorySystemText: '#475569',
  // Special status-change action badges. Kept as their own semantic tokens (rather than reusing
  // Attendance/Assignment) so cancel/futile/reopen styling can diverge independently later.
  HistoryCancelledBg: '#fee2e2', // red — Job Cancelled / Job Futiled
  HistoryCancelledText: '#991b1b',
  HistoryReopenedBg: '#fef3c7', // amber — Job Re-Opened
  HistoryReopenedText: '#78350f',
  // V4 Status Badge fallback (WCAG AA compliant — 4.6:1 on white)
  StatusDefaultBg: '#f1f5f9',
  StatusDefaultText: '#475569',
  // Service Activity Monitor — cell heat ramp (4 steps, per-column normalised).
  // Two ramps: cool for ordinary workflow columns, warm for the three columns
  // that mean someone must act (New, Parts Required, Escalated) — the same
  // semantic the old blanket pink carried, but proportional to the count.
  // All eight steps are light tints, so ActivityHeatText clears 4.5:1 on every
  // one of them (measured worst case: #0f172a on #fdba74 = 10.59:1).
  ActivityHeatCool1: '#f0fdfa',
  ActivityHeatCool2: '#ccfbf1',
  ActivityHeatCool3: '#99f6e4',
  ActivityHeatCool4: '#5eead4',
  ActivityHeatWarm1: '#fff7ed',
  ActivityHeatWarm2: '#ffedd5',
  ActivityHeatWarm3: '#fed7aa',
  ActivityHeatWarm4: '#fdba74',
  ActivityHeatText: '#0f172a', // ≥10.5:1 on every ramp step above
  // Empty cells still render a real `0`, so this is body text under WCAG 1.4.3
  // and needs 4.5:1 — not a decorative grey. #475569 measures 7.58:1 on white
  // and 7.14:1 on the palest heat tint, while staying visibly lighter than
  // ActivityHeatText so zeros recede.
  ActivityZeroText: '#475569',

  // Flow-rail lane hues. Bar fills may use the mid tones; lane *label* text
  // must use the Text variants, which are the ones measured against white.
  // The teal lane has no entry here — it uses the theme's `v4.accent` /
  // `v4.accentDark` pair, which already encodes that same fill/text split.
  ActivityLaneParts: '#4f46e5', // bar fill
  ActivityLanePartsText: '#3730a3', // 9.7:1 on white
  ActivityLaneException: '#dc2626', // bar fill
  ActivityLaneExceptionText: '#b91c1c', // 6.4:1 on white
  ActivityRailTrack: '#e2e8f0', // magnitude-bar track

  // V4 Tag colors (Slate Professional)
  TagBlueBg: '#dbeafe',
  TagBlueText: '#1e40af',
  TagGreenBg: '#dcfce7',
  TagGreenText: '#14532d',
  TagAmberBg: '#fef3c7',
  TagAmberText: '#78350f',
  TagPurpleBg: '#f3e8ff',
  TagPurpleText: '#6b21a8',
  TagRedBg: '#fee2e2',
  TagRedText: '#991b1b',
  TagIndigoBg: '#e0e7ff',
  TagIndigoText: '#3730a3',
};
